import { CompositeScreenProps, NavigationProp } from '@react-navigation/native'
import { createStackNavigator, StackScreenProps } from '@react-navigation/stack'
import React, { lazy } from 'react'
import { useTranslation } from 'react-i18next'

import CustomHeaderBackButton from '../../components/CustomHeaderBackButton/CustomHeaderBackButton'
import { useAuth } from '../../context/auth'
import { useScreenOptions, useWhiteScreenOptions } from '../../hooks/use-screen-options'
import { isUserAuthenticated } from '../../util/auth'
import { getUserProfileFromEmrId, getUserProfileText } from '../../util/profile'
import styles from '../styles'

import { MainTabParamList, MainTabScreenProps } from '.'

const Consultation = lazy(() => import('../../screens/ConsultationSummary'))
const Consultations = lazy(() => import('../../screens/Consultations'))
const PatientConsultation = lazy(() => import('../../screens/PatientConsultation'))
const MedicalHistory = lazy(() => import('../../screens/MedicalHistory'))
const Welcome = lazy(() => import('../../screens/Welcome'))

export type HistoryStackParamList = {
  AuthRedirect: undefined
  Consultations: undefined
  Consultation: {
    id: number
  }
  Order: {
    id: string
    reason?: 'attachment'
  }
  LoggedOut: undefined
  MedicalHistory: {
    emrId: string
  }
  Welcome: undefined
}

export type HistoryStackScreenProps<T extends keyof HistoryStackParamList> = CompositeScreenProps<
  StackScreenProps<HistoryStackParamList, T>,
  MainTabScreenProps<keyof MainTabParamList>
>

export const historyStackLinking = {
  initialRouteName: 'Consultations' as const,
  path: '/consultations',
  screens: {
    Consultations: '/',
    Consultation: '/:id',
    LoggedOut: '/logged-out',
    MedicalHistory: '/medical-history',
    Order: '/orders/:id',
    Welcome: '/welcome',
  },
}

const HistoryStack = createStackNavigator<HistoryStackParamList>()

export function History() {
  const { t } = useTranslation()
  const { isDependantAccount, profile } = useAuth()
  const screenOptions = useScreenOptions()
  const whiteScreenOptionsNoHeader = useWhiteScreenOptions(true)

  const getMedicalHistoryTitle = (route: HistoryStackScreenProps<'MedicalHistory'>['route']) => {
    const patient = getUserProfileFromEmrId(profile, route.params.emrId)
    // We are limiting the length of the name to avoid having 2 line titles in small screens
    const patientName = getUserProfileText({ user: patient, config: { maximumLengthAllowed: 20 } })

    return isDependantAccount(patient?.id)
      ? t('screens.medicalHistory.dependantTitle', { patientName })
      : t('screens.medicalHistory.title')
  }

  const getHeaderBackButton = (navigation: NavigationProp<HistoryStackParamList>) => (
    <CustomHeaderBackButton callRedirect={() => navigation.navigate('Consultations')} navigation={navigation} />
  )

  return (
    <HistoryStack.Navigator
      initialRouteName={isUserAuthenticated({ profile }) ? 'Consultations' : 'Welcome'}
      screenOptions={screenOptions}
    >
      {isUserAuthenticated({ profile }) ? (
        <>
          <HistoryStack.Screen
            name="Consultations"
            component={Consultations}
            options={{ title: t('screens.consultations.title'), headerShown: false }}
          />
          <HistoryStack.Screen
            name="Consultation"
            component={Consultation}
            options={({ navigation }) => ({
              title: t('screens.consultation.title'),
              headerTitleStyle: styles.headerTitle,
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <HistoryStack.Screen
            name="MedicalHistory"
            component={MedicalHistory}
            options={({ navigation, route }) => ({
              title: getMedicalHistoryTitle(route),
              headerTitleStyle: styles.headerTitle,
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <HistoryStack.Screen
            name="Order"
            component={PatientConsultation}
            options={{ title: t('screens.patientConsultation.title'), headerShown: false }}
          />
        </>
      ) : (
        <HistoryStack.Screen name="Welcome" component={Welcome} options={whiteScreenOptionsNoHeader} />
      )}
    </HistoryStack.Navigator>
  )
}

export default History
